import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import type { OfferLanguage, SendQuotePayload } from '@/features/quotes/api/useQuote'
import type { QuoteDetail } from '@/types/domain'

/* Sending the commercial offer (FR-71, Story 2.6).
 *
 * A sheet rather than a bare button press, for the reason the case status send
 * is a sheet: an email leaves permanently, and the address it leaves for must
 * be on screen BEFORE anyone commits. The press that issues Rev A is the least
 * reversible action in the product — it snapshots an immutable revision, renders
 * a PDF and writes to a client — so the one thing this surface owes the user is
 * a last look at where it is going.
 *
 * It deliberately asks almost nothing. The recipient defaults to the client's
 * primary contact; the sheet exists to SHOW those defaults, not to make somebody
 * re-enter them. Sending is one more click from opening it.
 *
 * The LANGUAGE is not this sheet's state, and used to be. It held its own
 * `useState('ru')`, so a reader who had just checked the English preview met a
 * send form set to Russian and issued the document they had not read. It is the
 * page's now, shared with the preview sheet, which is the only arrangement in
 * which "what I checked is what goes out" is true — see QuoteDetailPage.
 */

const LANGUAGES = ['ru', 'en'] as const satisfies readonly OfferLanguage[]

export function OfferSendSheet({
  open,
  onClose,
  quote,
  onSubmit,
  pending,
  submitError,
  language,
  onLanguageChange,
}: {
  open: boolean
  onClose: () => void
  quote: QuoteDetail
  onSubmit: (values: SendQuotePayload) => void
  pending?: boolean
  submitError?: string
  /** The language the offer prints in — the page's, shared with the preview. */
  language: OfferLanguage
  onLanguageChange: (language: OfferLanguage) => void
}) {
  const { t } = useTranslation()

  /* The address the offer goes to, as the server would resolve it. Absent when
     the client has no contact carrying an email — which is a refusal rather
     than a blank field, because the fix is in the directory and not here. */
  const recipient = quote.clientContactEmail?.trim() ?? ''
  const noRecipient = recipient === ''

  return (
    <FormSheet
      open={open}
      /* The language is NOT reset on close. It belongs to the page and the
         preview reads the same value: resetting it here would silently undo a
         choice the reader made in the sheet they came from. */
      onClose={onClose}
      title={t('quotes.offerSend.title')}
      submitLabel={t('quotes.actions.sendOffer')}
      onSubmit={() =>
        onSubmit({
          // The recipient is deliberately NOT sent when it is the default one:
          // an empty list tells the server "the client's primary contact", so a
          // contact corrected between opening this sheet and pressing send is
          // honoured rather than overridden by a stale address.
          language,
        })
      }
      pending={pending}
      disabled={noRecipient}
      error={submitError}
      notice={noRecipient ? t('quotes.offerSend.noRecipient') : undefined}
    >
      <div className="flex flex-col gap-4">
        {/* What is about to be issued. The revision letter is the fact the user
            is least likely to be holding in their head, and it is what the
            client will quote back. */}
        <div className="border-line bg-surface-sunken flex flex-col gap-1.5 rounded-lg border px-4 py-3">
          <span className="asa-eyebrow">{t('quotes.offerSend.document')}</span>
          <span className="text-text-heading text-[13px] leading-snug">
            {quote.number}
            {quote.revisions && quote.revisions.length > 0 ? (
              <span className="text-text-muted">
                {' '}
                {t('quotes.offerSend.nextRevision', {
                  label: nextLabel(quote.revisions.length),
                })}
              </span>
            ) : null}
          </span>
          {quote.validUntil ? (
            <span className="text-text-muted text-[12px] leading-snug">
              {t('quotes.offerSend.validUntil', { date: quote.validUntil })}
            </span>
          ) : null}
        </div>

        {noRecipient ? null : (
          <div className="border-line bg-surface-sunken flex flex-col gap-1.5 rounded-lg border px-4 py-3">
            <span className="asa-eyebrow">{t('quotes.offerSend.recipient')}</span>
            <span className="text-text-heading text-[13px] leading-snug">
              {quote.clientContactName ? (
                <>
                  {quote.clientContactName}{' '}
                  <span className="text-text-muted">&lt;{recipient}&gt;</span>
                </>
              ) : (
                recipient
              )}
            </span>
          </div>
        )}

        <Field label={t('cases.statusSend.language')}>
          {(props) => (
            <Select
              {...props}
              value={language}
              onChange={(next) => onLanguageChange(next as OfferLanguage)}
              options={LANGUAGES.map((code) => ({
                value: code,
                label: t(code === 'ru' ? 'common.language.russian' : 'common.language.english'),
              }))}
            />
          )}
        </Field>

        <p className="text-text-muted text-[12px] leading-snug">
          {t('quotes.offerSend.explainer')}
        </p>
      </div>
    </FormSheet>
  )
}

/** The letter the next revision will carry: A, B, … Z, AA.
 *
 * Mirrors RevisionLabel in the backend, and is shown rather than relied upon —
 * the server mints the real one inside the transaction, so this is a preview.
 * Divergence would be cosmetic and the server always wins. */
function nextLabel(issued: number): string {
  let ordinal = issued + 1
  let label = ''
  while (ordinal > 0) {
    ordinal -= 1
    label = String.fromCharCode(65 + (ordinal % 26)) + label
    ordinal = Math.floor(ordinal / 26)
  }
  return label
}
